import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, IconButton, Paper, styled } from '@mui/material';
import { ReactNode, useId } from 'react';
import { Link } from 'react-router-dom';
import { SectionHeading } from '@/components/PartFinder/SectionHeading';
import { useTileCollapsed } from './useTileCollapsed';

const TileCard = styled(Paper, { shouldForwardProp: property => property !== 'isCollapsed' })<{
  isCollapsed: boolean;
}>(({ isCollapsed }) => ({
  // A tile with an empty state still has to look like a tile.
  minHeight: isCollapsed ? undefined : '168px',
  height: isCollapsed ? 'fit-content' : undefined,
  padding: '16px',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
}));

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});

const HeaderActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const TileBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  paddingTop: '12px',
});

const HeadingLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  alignSelf: 'flex-start',
  borderRadius: '6px',
  '&:hover span': {
    color: theme.palette.secondary.main,
  },
}));

export const DashboardTile = ({
  id,
  title,
  icon,
  to,
  action,
  children,
}: {
  id: string;
  title: string;
  icon: ReactNode;
  to?: string;
  action?: ReactNode;
  children: ReactNode;
}) => {
  const { isCollapsed, toggleCollapsed } = useTileCollapsed(id);
  const bodyId = useId();

  return (
    <TileCard variant='outlined' isCollapsed={isCollapsed}>
      <HeaderRow>
        {to ? (
          <HeadingLink to={to} title={`Go to ${title.toLowerCase()}`}>
            <SectionHeading variant='section' icon={icon} title={title} />
          </HeadingLink>
        ) : (
          <SectionHeading variant='section' icon={icon} title={title} />
        )}
        <HeaderActions>
          {action}
          <IconButton
            size='small'
            aria-label={`Toggle ${title.toLowerCase()}`}
            aria-expanded={!isCollapsed}
            aria-controls={bodyId}
            onClick={toggleCollapsed}
          >
            {isCollapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </HeaderActions>
      </HeaderRow>
      <Collapse in={!isCollapsed} id={bodyId}>
        <TileBody>{children}</TileBody>
      </Collapse>
    </TileCard>
  );
};
